import { BreadcrumbItem } from '@/components/Breadcrumb';
import styles from '@/pages/jobs/style.module.scss';
import { getJobSpecialities } from '@/services/cmsService';
import {
  KNOWN_CANONICAL_LOCATION_SLUGS,
  SimilarSearchJobSummary,
  buildCanonicalJobListingUrl,
  buildJobsListingBreadcrumbItems,
  buildJobsListingMetaDescription,
  buildJobsListingMetaTitle,
  createKnownSpecialitySlugs,
  derivePageHeaderContext,
  extractPerfectlyMatchingResults,
  extractSimilarJobs,
  filterCanonicalSpecialitySummaries,
  getLocationNameFromCanonicalSlug,
  getLocationQueryValueFromCanonicalSlug,
  getWorkTypeFromCanonicalSlug,
  isCanonicalCitySlug,
  parseQueryForGetJobs,
  toCanonicalSlug,
  toSimilarSearchJobSummaries,
} from '@/utils/jobListingHelper';
import { GetServerSidePropsContext } from 'next';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import { GetServerSidePropsResult } from 'next/types';
import React from 'react';
import { getAllJobSpecialitiesFromPicklist, getJobs } from 'services/jobService';
import { searchLocationForServer } from 'services/locationService.server';
import { sentence, title } from 'to-case';
import { IArticleSummary, IFrequentlyAskedQuestion } from 'types/cms';
import { IJobDetail, IJobSpeciality, JobSearchCriteria, JobSearchWebCriteria } from 'types/jobs';
import { getDoctorTestimonials } from 'utils/getDoctorTestimonials';
import { getDoctorsFaqs } from 'utils/getDoctorsFaqs';
import { getGuidesForContext } from 'utils/getGuidesForContext';
import { getJobsForExploreSimilarSearches } from 'utils/getJobsForExploreSimilarSearches';
import { getRequestOrigin } from 'utils/getRequestOrigin';
import {
  specialCharSpecialities,
  specialCharSpecialitiesAndSubSpecialties,
} from 'utils/specialities-mapping';

import JobListing from 'modules/JobListing';
import { filterFaqsForContext } from 'modules/JobListing/utils/filterFaqsForContext';
import {
  TestimonialsForContext,
  filterTestimonialsForContext,
} from 'modules/JobListing/utils/filterTestimonialsForContext';
import { ContainerStyle, UserLoggedinContainer } from 'modules/UserLoggedinContainer';

export interface JobsListProps {
  jobs: {
    total: number;
    data: IJobDetail[];
    limit: number;
  };
  similarJobs: IJobDetail[];
  jobSpecialities: IJobSpeciality[];
  query: JobSearchWebCriteria;
  guides: IArticleSummary[];
  // Already filtered/ranked against the page's search context (see derivePageHeaderContext) -
  // getServerSideProps ships only the subset JobListing actually renders, rather than the whole
  // CMS collection.
  faqs: IFrequentlyAskedQuestion[];
  testimonialsForContext: TestimonialsForContext;
  breadcrumbItems: BreadcrumbItem[];
  metadata: {
    title: string;
    description: string;
    canonicalUrl: string;
    nextUrl?: string;
    prevUrl?: string;
    noIndex: boolean;
  };
  origin: string;
  similarSearchJobs?: SimilarSearchJobSummary[];
}

const DEFAULT_CITY_RADIUS_KM = '50';

const shouldResolveCoordinatesForLocation = (locationName?: string) => {
  if (!locationName) {
    return false;
  }

  const normalizedLocationSlug = toCanonicalSlug(locationName);

  // Resolve coordinates for known city slugs and unknown slugs.
  // Skip known broad regions (countries/states) that should stay as location filters.
  return (
    isCanonicalCitySlug(normalizedLocationSlug) ||
    !KNOWN_CANONICAL_LOCATION_SLUGS.has(normalizedLocationSlug)
  );
};

type ResolvedCityCoordinates = { lat: string; lon: string; state?: string } | undefined;

// Caches geo lookups so a crawler sweeping sitemap-listed city URLs doesn't gate TTFB on an
// external geo lookup for the same string on every request. shouldResolveCoordinatesForLocation
// resolves coordinates for *any* unrecognized location slug, not just the 8 canonical cities, so
// this is capped (FIFO eviction of the oldest entry) rather than left unbounded - otherwise a bot
// sweeping unique /jobs/listing/<random-slug> URLs would grow this map and issue one external
// geo lookup per unique slug forever.
const CITY_COORDINATES_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const CITY_COORDINATES_CACHE_MAX_ENTRIES = 500;
const cityCoordinatesCache = new Map<
  string,
  { coordinates: ResolvedCityCoordinates; expiresAt: number }
>();

// Test-only escape hatch: the cache is process-lifetime by design, but that makes different
// tests asserting different geo lookups for the same city slug bleed into each other.
export const resetCityCoordinatesCacheForTests = () => cityCoordinatesCache.clear();

const resolveCoordinatesForCity = async (
  city: string,
  ctx: GetServerSidePropsContext,
): Promise<ResolvedCityCoordinates> => {
  const cacheKey = toCanonicalSlug(city);
  const cached = cityCoordinatesCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.coordinates;
  }

  const coordinates = await resolveCoordinatesForCityUncached(city, ctx);

  if (
    !cityCoordinatesCache.has(cacheKey) &&
    cityCoordinatesCache.size >= CITY_COORDINATES_CACHE_MAX_ENTRIES
  ) {
    const oldestKey = cityCoordinatesCache.keys().next().value;
    if (oldestKey !== undefined) {
      cityCoordinatesCache.delete(oldestKey);
    }
  }

  cityCoordinatesCache.set(cacheKey, {
    coordinates,
    expiresAt: Date.now() + CITY_COORDINATES_CACHE_TTL_MS,
  });

  return coordinates;
};

const resolveCoordinatesForCityUncached = async (
  city: string,
  ctx: GetServerSidePropsContext,
): Promise<ResolvedCityCoordinates> => {
  try {
    const targetCitySlug = toCanonicalSlug(city);
    const locationResult = await searchLocationForServer(city, ctx);
    const results = locationResult.results || [];
    const resultsWithPosition = results.filter(result => Boolean(result.position));

    // Two passes so an exact match always wins over a substring match, regardless of result
    // order - otherwise an earlier substring hit (e.g. "Perthville" for "perth") beats a later
    // exact one, radius-filtering around the wrong place while the page is headlined correctly.
    const exactCityMatch = resultsWithPosition.find(result => {
      const resultNameSlug = toCanonicalSlug(result.name);
      const resultCitySlug = result.city ? toCanonicalSlug(result.city) : '';

      return resultNameSlug === targetCitySlug || resultCitySlug === targetCitySlug;
    });

    const closeCityMatch = resultsWithPosition.find(result => {
      const resultNameSlug = toCanonicalSlug(result.name);
      const resultCitySlug = result.city ? toCanonicalSlug(result.city) : '';

      return (
        resultNameSlug.includes(targetCitySlug) ||
        targetCitySlug.includes(resultNameSlug) ||
        resultCitySlug.includes(targetCitySlug) ||
        targetCitySlug.includes(resultCitySlug)
      );
    });

    const locationWithPosition = exactCityMatch || closeCityMatch || resultsWithPosition[0];

    if (!locationWithPosition?.position) {
      return undefined;
    }

    return {
      lat: String(locationWithPosition.position.lat),
      lon: String(locationWithPosition.position.lon),
      // Read state off the matched result itself, not the first result in the list to have any
      // state - otherwise a same-named place in another state/country can pair the matched
      // coordinates with the wrong state label (e.g. NZ coordinates with an Australian state).
      state: locationWithPosition.state || '',
    };
  } catch {
    return undefined;
  }
};

const JobsList = ({
  jobs,
  similarJobs,
  query,
  jobSpecialities,
  breadcrumbItems,
  metadata,
  origin,
  guides,
  similarSearchJobs = [],
  faqs,
  testimonialsForContext,
}: JobsListProps) => {
  return (
    <UserLoggedinContainer
      size={false}
      className={styles.jobListContainer}
      style={ContainerStyle.NO_BANNER}
      fullWidth={true}
      noFooterMargin
    >
      <Head>
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
        {metadata.nextUrl && <link rel="next" href={metadata.nextUrl} />}
        {metadata.prevUrl && <link rel="prev" href={metadata.prevUrl} />}
      </Head>
      <NextSeo
        canonical={metadata.canonicalUrl}
        noindex={metadata.noIndex}
        openGraph={{
          title: metadata.title,
          description: metadata.description,
          type: 'website',
        }}
      />
      <JobListing
        jobs={jobs}
        similarJobs={similarJobs}
        query={query}
        jobSpecialities={jobSpecialities}
        breadcrumbItems={breadcrumbItems}
        origin={origin}
        guides={guides}
        similarSearchJobs={similarSearchJobs}
        faqs={faqs}
        testimonialsForContext={testimonialsForContext}
      />
    </UserLoggedinContainer>
  );
};

export async function getServerSideProps(
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<JobsListProps>> {
  const { query } = ctx;
  const { listing, ...queryinfo } = query;
  const [jobSpecialities, picklistSpecialities] = await Promise.all([
    getJobSpecialities(),
    getAllJobSpecialitiesFromPicklist(),
  ]);
  // The CMS list only covers editor-curated root categories - the v1/v2 picklist also has
  // specialities (e.g. "Cardiology") that appear standalone in existing/crawled listing URLs.
  // Without merging both in, a segment for one of those falls through to location
  // classification below (see shouldResolveCoordinatesForLocation) instead of being recognized
  // as a speciality.
  const knownSpecialitySlugs = new Set([
    ...createKnownSpecialitySlugs(jobSpecialities ?? []),
    ...picklistSpecialities.map(toCanonicalSlug),
  ]);

  const filteredKeys: {
    grade?: string;
    speciality?: string;
    rootSpeciality?: string;
    subspeciality?: string;
    location?: string;
    locationName?: string;
    workType?: string;
    locationSlug?: string;
    workTypeSlug?: string;
  } = parseListingSegments((listing as string[]) || [], query, knownSpecialitySlugs);

  if (filteredKeys.grade && filteredKeys.grade === 'smo-moss') {
    queryinfo.grade = 'SMO/MOSS'; // Hard coded
  } else if (filteredKeys.grade) {
    queryinfo.grade = title(filteredKeys.grade);
  }

  if (filteredKeys.speciality) {
    queryinfo.speciality =
      specialCharSpecialities[filteredKeys.speciality] ?? sentence(filteredKeys.speciality);
  }

  if (filteredKeys.rootSpeciality) {
    // API still expects speciality
    queryinfo.rootSpeciality =
      specialCharSpecialitiesAndSubSpecialties[filteredKeys.rootSpeciality] ??
      title(filteredKeys.rootSpeciality);
  }
  if (filteredKeys.subspeciality) {
    queryinfo.subspeciality =
      specialCharSpecialitiesAndSubSpecialties[filteredKeys.subspeciality] ??
      title(filteredKeys.subspeciality);
  }

  if (filteredKeys.location) {
    queryinfo.location = filteredKeys.location;
  }

  if (filteredKeys.locationName) {
    queryinfo.locationName = filteredKeys.locationName;
  }

  if (filteredKeys.workType) {
    queryinfo.workType = filteredKeys.workType;
  }

  const convertedQuery = parseQueryForGetJobs(queryinfo);

  // Never geocode over the top of coordinates the request already carries - e.g. a user's exact
  // suburb selection (lat/lon in the query) must not be replaced by the first same-named result
  // an external geocoder happens to return (which can be in a different state/country).
  const hasExistingCoordinates =
    typeof queryinfo.lat === 'string' && typeof queryinfo.lon === 'string';

  const cityFilter =
    !hasExistingCoordinates &&
    typeof queryinfo.locationName === 'string' &&
    shouldResolveCoordinatesForLocation(queryinfo.locationName)
      ? queryinfo.locationName
      : undefined;

  if (cityFilter) {
    const cityCoordinates = await resolveCoordinatesForCity(cityFilter, ctx);

    if (cityCoordinates) {
      delete queryinfo.location;
      queryinfo.lat = cityCoordinates.lat;
      queryinfo.lon = cityCoordinates.lon;
      queryinfo.radius =
        typeof queryinfo.radius === 'string' && queryinfo.radius.length > 0
          ? queryinfo.radius
          : DEFAULT_CITY_RADIUS_KM;
      queryinfo.state = cityCoordinates.state || '';

      convertedQuery.location = undefined;
      convertedQuery.lat = cityCoordinates.lat;
      convertedQuery.lon = cityCoordinates.lon;
      convertedQuery.radius =
        typeof convertedQuery.radius === 'string' && convertedQuery.radius.length > 0
          ? convertedQuery.radius
          : DEFAULT_CITY_RADIUS_KM;
    }
  }

  const exploreSimilarSearchesCriteria: JobSearchCriteria = {
    grade: convertedQuery.grade as JobSearchCriteria['grade'],
    workType: convertedQuery.workType as JobSearchCriteria['workType'],
  };

  // Computed up front (it's a pure function of queryinfo, no CMS/API dependency) so
  // getGuidesForContext can join the same Promise.all below instead of running as an extra
  // sequential round trip.
  const pageHeaderContext = derivePageHeaderContext(queryinfo);

  const [jobs, guides, exploreSimilarSearchesJobs, faqs, testimonials] = await Promise.all([
    getJobs(convertedQuery, ctx),
    getGuidesForContext(pageHeaderContext),
    getJobsForExploreSimilarSearches(exploreSimilarSearchesCriteria, ctx),
    getDoctorsFaqs(),
    getDoctorTestimonials(),
  ]);

  const perfectlyMatchingJobs = extractPerfectlyMatchingResults(jobs);

  // Rank/cap the CMS testimonials + FAQs against the page's search context here, server-side, so
  // only the subset JobListing actually renders gets serialized into the page props - shipping
  // getDoctorTestimonials()/getDoctorsFaqs()'s full collated-across-every-CMS-page result to the
  // client just to filter it down client-side was bloating this page's hydration payload well
  // past Next's recommended size. Guides are already ranked by getGuidesForContext.
  const testimonialsForContext = filterTestimonialsForContext(testimonials, pageHeaderContext);
  const contextFaqs = filterFaqsForContext(faqs, pageHeaderContext);

  const description = buildJobsListingMetaDescription({
    count: perfectlyMatchingJobs.total,
    grade: convertedQuery.grade as string | undefined,
    speciality: convertedQuery.speciality as string | undefined,
    location: convertedQuery.locationName as string | undefined,
    workType: convertedQuery.workType as string | undefined,
  });
  const metaTitle = buildJobsListingMetaTitle({
    count: perfectlyMatchingJobs.total,
    grade: convertedQuery.grade as string | undefined,
    speciality: convertedQuery.speciality as string | undefined,
    location: convertedQuery.locationName as string | undefined,
    workType: convertedQuery.workType as string | undefined,
  });

  // Build origin from current request domain (localhost for dev, production domain for prod)
  const origin = getRequestOrigin(ctx);

  const canonicalUrl = buildCanonicalJobListingUrl(
    {
      grade: queryinfo.grade as string | undefined,
      speciality: (queryinfo.speciality || queryinfo.rootSpeciality) as string | undefined,
      location: queryinfo.locationName as string | undefined,
      workType: queryinfo.workType as string | undefined,
    },
    origin,
  );

  // Page 2+ always canonicalises back to the page 1 base URL above, since
  // buildCanonicalJobListingUrl never factors in the page query param.
  const currentPage = Number(convertedQuery.page) || 1;
  const totalPages = Math.ceil(perfectlyMatchingJobs.total / perfectlyMatchingJobs.limit) || 1;
  const buildPaginatedUrl = (page: number) =>
    page > 1 ? `${canonicalUrl}?page=${page}` : canonicalUrl;
  const nextUrl = currentPage < totalPages ? buildPaginatedUrl(currentPage + 1) : undefined;
  const prevUrl = currentPage > 1 ? buildPaginatedUrl(currentPage - 1) : undefined;
  const noIndex = currentPage > 1;

  // Build breadcrumbs with gap-filling logic for missing hierarchy levels
  const breadcrumbItems = buildJobsListingBreadcrumbItems(
    {
      grade: queryinfo.grade as string | undefined,
      speciality: (queryinfo.speciality || queryinfo.rootSpeciality) as string | undefined,
      location: queryinfo.locationName as string | undefined,
      workType: queryinfo.workType as string | undefined,
    },
    origin,
  );

  return {
    props: {
      jobs: perfectlyMatchingJobs,
      similarJobs: extractSimilarJobs(jobs.data),
      jobSpecialities: jobSpecialities ?? [],
      query: queryinfo,
      breadcrumbItems,
      guides,
      faqs: contextFaqs,
      testimonialsForContext,
      metadata: {
        title: metaTitle,
        description,
        canonicalUrl,
        noIndex,
        // getServerSideProps props must be JSON-serializable - omit the key entirely rather
        // than setting it to `undefined`, which throws.
        ...(nextUrl ? { nextUrl } : {}),
        ...(prevUrl ? { prevUrl } : {}),
      },
      origin,
      similarSearchJobs: filterCanonicalSpecialitySummaries(
        toSimilarSearchJobSummaries(exploreSimilarSearchesJobs),
        knownSpecialitySlugs,
      ),
    },
  };
}

export default JobsList;

const KNOWN_GRADE_SLUGS = new Set([
  'medical-student',
  'resident-medical-officer',
  'registrar',
  'smo-moss',
  'specialist-consultant',
  'clinical-director',
  'director-of-medical-services',
]);

// Known work types
const KNOWN_WORK_TYPE_SLUGS = new Set(['locum', 'permanent']);

const hasLocationCoordinates = (query: GetServerSidePropsContext['query']) => {
  return typeof query.lat === 'string' && typeof query.lon === 'string';
};

/**
 * Refactored to use value-based classification instead of position-based.
 * Classifies each listing segment by checking against known sets:
 * - If it matches a workType (locum, permanent), it's a workType
 * - If it matches a grade, it's a grade
 * - If it matches a speciality, it's a speciality
 * - If it matches a location, it's a location
 * - Otherwise, it's treated as a location
 */
const parseListingSegments = (
  listing: Array<string>,
  query: GetServerSidePropsContext['query'],
  knownSpecialitySlugs: Set<string>,
) => {
  if (!listing || listing.length === 0) {
    return {};
  }

  const result: {
    grade?: string;
    speciality?: string;
    rootSpeciality?: string;
    subspeciality?: string;
    location?: string;
    locationName?: string;
    workType?: string;
    locationSlug?: string;
    workTypeSlug?: string;
  } = {};

  const queryLocationName =
    typeof query.locationName === 'string' && query.locationName.length > 0
      ? (query.locationName as string)
      : undefined;

  // Classify each segment by value
  const classifications = listing.map(segment => {
    if (KNOWN_WORK_TYPE_SLUGS.has(segment)) {
      return 'workType';
    }
    if (KNOWN_GRADE_SLUGS.has(segment)) {
      return 'grade';
    }
    if (knownSpecialitySlugs.has(segment)) {
      return 'speciality';
    }
    if (KNOWN_CANONICAL_LOCATION_SLUGS.has(segment) || hasLocationCoordinates(query)) {
      return 'location';
    }
    // Default to location if not recognized
    return 'location';
  });

  // Process classified segments
  let specialityCount = 0;
  let locationCount = 0;

  for (let i = 0; i < listing.length; i++) {
    const segment = listing[i];
    const classification = classifications[i];

    switch (classification) {
      case 'grade':
        result.grade = segment;
        break;
      case 'workType': {
        const mappedWorkType = getWorkTypeFromCanonicalSlug(segment);
        if (mappedWorkType) {
          result.workType = mappedWorkType;
        }
        break;
      }
      case 'speciality':
        specialityCount++;
        if (specialityCount === 1) {
          result.rootSpeciality = segment;
        } else if (specialityCount === 2) {
          result.locationSlug = segment;
          result.subspeciality = segment;
        }
        break;
      case 'location':
        locationCount++;
        if (locationCount === 1) {
          result.location = getLocationQueryValueFromCanonicalSlug(segment);
          result.locationName = queryLocationName || getLocationNameFromCanonicalSlug(segment);
        }
        // Ignore additional locations if there are more than one
        break;
      default:
        break;
    }
  }

  const getSingleQueryValue = (value: string | string[] | undefined): string | undefined => {
    if (typeof value === 'string') {
      return value;
    }
    return Array.isArray(value) ? value[0] : undefined;
  };

  // Extract location and workType from query params (passed by [work-type].js adapter)
  if (!result.location && query.location) {
    result.location = getSingleQueryValue(query.location);
  }

  if (!result.locationName && query.locationName) {
    result.locationName = getSingleQueryValue(query.locationName);
  }

  if (!result.workType && query.workType) {
    result.workType = getSingleQueryValue(query.workType);
  }

  // Extract workTypeSlug from the listing array if not already found
  if (!result.workTypeSlug && query.workType) {
    // Try to convert display value back to slug
    const workTypeStr = getSingleQueryValue(query.workType);
    if (workTypeStr) {
      const slug = toCanonicalSlug(workTypeStr);
      if (KNOWN_WORK_TYPE_SLUGS.has(slug)) {
        result.workTypeSlug = slug;
      }
    }
  }

  // Extract locationSlug if not already found
  if (!result.locationSlug && query.locationName) {
    // Try to convert location name back to slug
    const locationNameStr = getSingleQueryValue(query.locationName);
    if (locationNameStr) {
      const slug = toCanonicalSlug(locationNameStr);
      if (KNOWN_CANONICAL_LOCATION_SLUGS.has(slug)) {
        result.locationSlug = slug;
      }
    }
  }

  return result;
};
